import { differenceInCalendarDays, isEqual, isSameMonth, isSameYear, parseISO } from 'date-fns';
import format from 'date-fns/format';
import pluralize from 'pluralize';

import { CalendarEvent } from 'components/Pages/Admin/Itinerary/Details/DetailsChronologicalMode/EventCalendar/Calendar/utils/days-generator/types';
import { EActivityEventType, ETripEvent } from 'detailsv2/structure';
import i18n from 'i18n';
// eslint-disable-next-line import/named
import { head, isEmpty, isNil, last } from 'ramda';
import { IDetailsScheduleItems, INearestLeftRange, IParams, IRange } from 'utils/itineraryDetails/types';
import logger from 'utils/logger';

import { getLocalDateFromString } from '../date';
import { SCHEDULE_ITEM_TYPES, SCHEDULE_TYPES } from './constants';

export const isEmptyRange = (range: IRange | null) => isNil(range) || isEmpty(range) || isNil(range.start);
export const isCompleteRange = (range: IRange, isSingle: boolean) => {
  if (isEmptyRange(range)) {
    return false;
  }

  if (isSingle) {
    return !isNil(range.start);
  }

  return !isNil(range.end);
};

const getTimeRangeKeyNameSection = (scheduleType: string) => {
  if (scheduleType === SCHEDULE_TYPES.DATES) {
    return 'datesRange';
  }

  if (scheduleType === SCHEDULE_TYPES.DAYS) {
    return 'daysRange';
  }

  logger.error(`[getTimeRangeKeyNameSection] unknown type supplied: '${scheduleType}'`);
};

export const getTimeRangeAdapter = (scheduleType: string, scheduleItem: IDetailsScheduleItems) => {
  return scheduleItem[getTimeRangeKeyNameSection(scheduleType)!];
};

const addYearToFormatString = (str: string, shouldAdd: boolean = true) => `${str}${shouldAdd ? ', yyyy' : ''}`;

export const formatDate = (value: string | Date, { includeYear = true, dateFormat = 'MMMM d' } = {}) => {
  const date = value instanceof Date ? value : parseISO(value);

  return format(date, addYearToFormatString(dateFormat, includeYear));
};

// You won't be able to use these props simultaneously
type TFormatDatesRangeConfig =
  | {
      includeOnlyDifferentYears?: undefined;
      hideYears?: boolean;
    }
  | {
      includeOnlyDifferentYears?: boolean;
      hideYears?: undefined;
    };

/**
 * @deprecated - Use formatDatesRangeWithoutParseISO Instead
 */
export const formatDatesRange = (
  range: IRange,
  { includeOnlyDifferentYears = false, hideYears = false }: TFormatDatesRangeConfig = {},
  dateFormat: string = 'MMMM d',
) => {
  if (isEmptyRange(range)) return null;

  const parsedStart = parseISO(range.start!);

  if (isNil(range.end)) {
    return formatDate(parsedStart, { includeYear: !hideYears && !includeOnlyDifferentYears });
  }

  const parsedEnd = parseISO(range.end);

  if (isEqual(parsedStart, parsedEnd)) {
    return formatDate(parsedStart, { includeYear: !hideYears && !includeOnlyDifferentYears, dateFormat });
  }

  if (!isSameYear(parsedStart, parsedEnd)) {
    return `${formatDate(parsedStart, {
      includeYear: !hideYears,
      dateFormat,
    })} - ${formatDate(parsedEnd, { includeYear: !hideYears, dateFormat })}`;
  }

  if (isSameMonth(parsedStart, parsedEnd)) {
    return `${format(parsedStart, dateFormat)} - ${format(
      parsedEnd,
      addYearToFormatString('d', !hideYears && !includeOnlyDifferentYears),
    )}`;
  }

  return `${format(parsedStart, dateFormat)} - ${format(
    parsedEnd,
    addYearToFormatString(dateFormat, !hideYears && !includeOnlyDifferentYears),
  )}`;
};

export const formatDatesRangeWithoutParseISO = (
  range: IRange,
  {
    includeOnlyDifferentYears = false,
    translate = false,
    translationFormat = '%b %-d',
    translationDateFormat = 'mdy',
  } = {},
  dateFormat: string = 'MMMM d',
) => {
  if (isEmptyRange(range)) return null;
  const isDMY = translationDateFormat === 'dmy';

  const parsedStart = getLocalDateFromString(range.start!.slice(0, 10));

  if (isDMY) {
    // Swap the day and month
    translationFormat = formatToDMY(translationFormat);
  }

  if (isNil(range.end)) {
    if (translate) {
      return i18n.strftime(parsedStart, translationFormat);
    }
    return formatDate(parsedStart, { includeYear: !includeOnlyDifferentYears });
  }

  const parsedEnd = getLocalDateFromString(range.end?.slice(0, 10));

  if (isEqual(parsedStart, parsedEnd)) {
    if (translate) {
      return i18n.strftime(parsedStart, translationFormat);
    }
    return formatDate(parsedStart, { includeYear: !includeOnlyDifferentYears, dateFormat });
  }

  if (!isSameYear(parsedStart, parsedEnd)) {
    if (translate) {
      return `${i18n.strftime(parsedStart, translationFormat)} - ${i18n.strftime(parsedEnd, translationFormat)}`;
    }
    return `${formatDate(parsedStart)} - ${formatDate(parsedEnd)}`;
  }

  // Example: Jun 2 - 3, 2024
  if (isSameMonth(parsedStart, parsedEnd)) {
    if (translate) {
      const startFormat = translationFormat; // "Jun 2"
      const endFormat = '%-d'; // "3"
      const yearFormat = `${includeOnlyDifferentYears ? '' : `${isDMY ? '' : ','} %Y`}`; // ", 2024"

      // Example: 2 - 3 Jun, 2024
      if (isDMY) {
        // Swap the formats of parsedStart and parsedEnd
        return `${i18n.strftime(parsedStart, endFormat)} - ${i18n.strftime(parsedEnd, startFormat + yearFormat)}`;
      }

      return `${i18n.strftime(parsedStart, startFormat)} - ${i18n.strftime(parsedEnd, endFormat + yearFormat)}`;
    }
    return `${format(parsedStart, dateFormat)} - ${format(
      parsedEnd,
      addYearToFormatString('d', !includeOnlyDifferentYears),
    )}`;
  }

  if (translate) {
    return `${i18n.strftime(parsedStart, translationFormat)} - ${i18n.strftime(
      parsedEnd,
      translationFormat + `${includeOnlyDifferentYears ? '' : `${isDMY ? '' : ','} %Y`}`,
    )}`;
  }

  return `${format(parsedStart, dateFormat)} - ${format(
    parsedEnd,
    addYearToFormatString(dateFormat, !includeOnlyDifferentYears),
  )}`;
};

export const calcNumberOfNightsOfDatesRange = (range: IRange) => {
  if (isNil(range.end)) return 0;
  return differenceInCalendarDays(parseISO(range.end), parseISO(range.start!));
};

export const calcNumberOfNightsOfDaysRange = (range: IRange) => {
  if (isNil(range.end)) return 0;
  return +range.end - +range.start!;
};

export const formatDaysRange = ({ start, end }: IRange) => {
  if (isNil(end) || start === end) {
    return isNil(start) ? null : i18n.t('proposal_content.day', { count: Number(start), defaultValue: 'Day' });
  }

  return i18n.t('proposal_content.day_range', { start, end, defaultValue: `Day ${start} to Day ${end}` });
};

export const calcNumberOfNightsAdapter = (scheduleType: string, scheduleItemType: string, range: IRange) => {
  if (scheduleItemType === SCHEDULE_ITEM_TYPES.ACTIVITY) return 0;
  if (scheduleItemType === SCHEDULE_ITEM_TYPES.DAY_ROOM) return 0;

  if (scheduleItemType === SCHEDULE_ITEM_TYPES.OVERNIGHT) {
    if (scheduleType === SCHEDULE_TYPES.DAYS) {
      return calcNumberOfNightsOfDaysRange(range);
    }

    if (scheduleType === SCHEDULE_TYPES.DATES) {
      return calcNumberOfNightsOfDatesRange(range);
    }
  }
};

export const formatNumberOfNights = (num: number) => (num === 1 ? `${num} Night` : `${num} Nights`);

const SCHEDULE_TYPE_TO_TIME_RANGE_FORMAT_MAP = {
  [SCHEDULE_TYPES.DATES]: formatDatesRangeWithoutParseISO,
  [SCHEDULE_TYPES.DAYS]: formatDaysRange,
};

export const formatTimeRangeAdapter = (scheduleType: string, range: IRange, params: IParams) => {
  const formatRange = SCHEDULE_TYPE_TO_TIME_RANGE_FORMAT_MAP[scheduleType];

  return isEmptyRange(range) ? null : formatRange(range, params);
};

// Returns a format with the day and month swapped in i18n format
export const formatToDMY = (format: string) => format.replace(/(%[b|B])([^%]*)(%-?d)/g, '$3$2$1');

export const determineRangeStart = (
  scheduleType: string,
  scheduleItems: IDetailsScheduleItems[],
  itemId: string | number,
) => {
  const itemIdx = scheduleItems.findIndex((item) => item.id === itemId);
  if (itemIdx <= 0) {
    return null;
  }

  return scheduleItems.slice(0, itemIdx).reduceRight((acc: string | null, item: IDetailsScheduleItems) => {
    if (acc) return acc;

    if (item.type === SCHEDULE_ITEM_TYPES.ACTIVITY) {
      return acc;
    }

    const range = getTimeRangeAdapter(scheduleType, item);

    if (item.type === SCHEDULE_ITEM_TYPES.OVERNIGHT) {
      if (range && range.start && range.end) {
        return range.end;
      }
    }

    if (item.type === SCHEDULE_ITEM_TYPES.DAY_ROOM) {
      if (range && range.start) {
        return range.start;
      }
    }

    return acc;
  }, null);
};

export const findNearestLeftRangeForType = (
  scheduleType: string,
  scheduleItems: IDetailsScheduleItems[],
  scheduleItem: IDetailsScheduleItems,
) => {
  const itemIdx = scheduleItems.findIndex((item) => item.id === scheduleItem.id);
  if (itemIdx <= 0) {
    return null;
  }

  return scheduleItems.slice(0, itemIdx).reduceRight((acc: IRange | null, item: IDetailsScheduleItems) => {
    if (acc) return acc;
    if (item.type !== scheduleItem.type) return acc;

    const range = getTimeRangeAdapter(scheduleType, item);
    if (range && !isEmptyRange(range)) {
      return range;
    }

    return acc;
  }, null);
};

export const findMinMaxRangeItems = (scheduleType: string, scheduleItems: INearestLeftRange[]) => {
  const flattenItemRangeItems = scheduleItems.reduce((acc: string[], item: INearestLeftRange) => {
    const range = getTimeRangeAdapter(scheduleType, item);
    if (range && range.start) {
      acc.push(range.start);
    }
    if (range && range.end) {
      acc.push(range.end);
    }

    return acc;
  }, []);

  const SCHEDULE_TYPE_TO_COMPARE_FN_MAP = {
    [SCHEDULE_TYPES.DATES]: (a: string, b: string) => +parseISO(a) - +parseISO(b),
    [SCHEDULE_TYPES.DAYS]: (a: string, b: string) => +a - +b,
  };

  const sortedRangeItems = flattenItemRangeItems.sort(SCHEDULE_TYPE_TO_COMPARE_FN_MAP[scheduleType]);

  const minRangeItem = head(sortedRangeItems);
  const maxRangeItem = last(sortedRangeItems);

  return { minRangeItem: minRangeItem ?? null, maxRangeItem: maxRangeItem ?? null };
};

export const calcNumberOfNightsOfSchedule = (scheduleType: string, scheduleItems: INearestLeftRange[]) => {
  const { minRangeItem, maxRangeItem } = findMinMaxRangeItems(scheduleType, scheduleItems);
  if (isNil(minRangeItem) || isNil(maxRangeItem)) {
    return 0;
  }

  if (SCHEDULE_TYPES.DATES === scheduleType) {
    return calcNumberOfNightsOfDatesRange({ start: minRangeItem, end: maxRangeItem });
  }

  if (SCHEDULE_TYPES.DAYS === scheduleType) {
    return calcNumberOfNightsOfDaysRange({ start: minRangeItem, end: maxRangeItem });
  }
};
export const extractExtraRanges = (scheduleType: string, scheduleItems: IDetailsScheduleItems[], itemId?: string) => {
  return scheduleItems
    .filter(
      (item) =>
        item.type !== SCHEDULE_ITEM_TYPES.ACTIVITY &&
        item.id !== itemId &&
        !isEmptyRange(getTimeRangeAdapter(scheduleType, item)),
    )
    .map((item) => getTimeRangeAdapter(scheduleType, item));
};

export const getEventPlankTitle = (event: CalendarEvent) => {
  const { data, type, daysRange } = event;

  if (data) {
    if ('departureAirport' in data && 'arrivalAirport' in data) {
      return `${data.departureAirport.city} to ${data.arrivalAirport.city}`;
    }

    if ('departureFrom' in data && 'arrivalTo' in data) {
      return `${data?.departureFrom} to ${data?.arrivalTo}`;
    }
  }

  if (Array.isArray(event.attachedPages) && event.attachedPages.length > 0) {
    if (type === ETripEvent.Accommodation) {
      const titleParts = [`<span class="plankTitlePage">${event.attachedPages?.[0].name}&nbsp;</span>`];

      if (data && 'isAlternate' in data && data.isAlternate) {
        const isMultiDay = daysRange.end && daysRange.end - daysRange.start > 0;
        const plankTitleMetaClasses = isMultiDay ? 'fw-420' : 'fw-420 plankTitleMeta';
        titleParts.push(`<span class="${plankTitleMetaClasses}">(Alternate)</span>`);
        const plankTitleWrapperClasses = isMultiDay
          ? 'plankTitleWrapper'
          : 'plankTitleWrapper plankTitleWrapper-pageHidden';

        return `<span class="${plankTitleWrapperClasses}">${titleParts.join('')}</span>`;
      }

      const nightsCount = daysRange && daysRange.end && daysRange.start ? daysRange.end - daysRange.start : 0;

      if (nightsCount === 0) {
        titleParts.push('<i class="plankTitleMeta">- Dayroom</i>');

        return `<span class="plankTitleWrapper plankTitleWrapper-pageHidden">${titleParts.join('')}</span>`;
      }

      if (nightsCount > 0) {
        titleParts.push(`<i>- ${nightsCount} ${pluralize('Nights', nightsCount)}</i>`);

        return `<span class="plankTitleWrapper">${titleParts.join('')}</span>`;
      }
    }

    if (
      type === ETripEvent.Activity &&
      daysRange.start &&
      daysRange.end &&
      daysRange.start !== daysRange.end &&
      'title' in data
    ) {
      const daysCount = daysRange.end - daysRange.start + 1;

      return `<span class="plankTitleWrapper"><span class="plankTitlePage">${data.title}&nbsp;</span><i>- ${daysCount} Days</i></span>`;
    }

    return event.attachedPages?.[0].name;
  }

  return 'no-title';
};

export const checkIsExpandableEvent = (event: CalendarEvent) => {
  const isOneDayActivity =
    event.type === ETripEvent.Activity &&
    event.data &&
    'type' in event.data &&
    event.data.type !== EActivityEventType.MultiDay;
  const isAddedByApiFlight =
    event.type === ETripEvent.Flight && event.data && 'fromFsService' in event.data && event.data.fromFsService;

  if (event.type === ETripEvent.Information || event.data.isIDL || isAddedByApiFlight || isOneDayActivity) {
    return false;
  }

  return true;
};

export const setFullEventPlankBorderColor = (eventId: string | number, color: string) => {
  // eslint-disable-next-line
  const anotherPlankRows: NodeListOf<HTMLElement> = document.querySelectorAll(`[data-plank-tail-id="${eventId}"]`);
  anotherPlankRows.forEach((element) => {
    element.style.borderColor = color;
  });
};
